import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useRef } from 'react';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import AvatarChip from '../../../shared/avatar-chip';
import z from 'zod';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

interface SetForwardingActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
}
export const SetForwardingActionSchema = z.object({
  type: z.literal('Set_Forwarding'),
  destUser: z.string().min(1, 'A user is required.'),
});

export type SetForwardingActionType = z.infer<typeof SetForwardingActionSchema>;

// Type guard to check if an action is SetForwardingActionType
function isSetForwardingAction(
  action: PolicyAction
): action is SetForwardingActionType {
  return action.type === 'Set_Forwarding';
}

export default function SetForwardingAction({
  setValue,
  watch,
  errors,
  control,
}: SetForwardingActionProps) {
  const autocompleteKey = useRef(new Date().getTime().toString());
  const actionData = watch('actionData');
  const idx = actionData.map((action) => action.type).indexOf('Set_Forwarding');
  const actionAtIndex = actionData[idx];
  if (!isSetForwardingAction(actionAtIndex)) {
    return null;
  }

  const currentAction: SetForwardingActionType = actionAtIndex;
  const error: FieldErrors<SetForwardingActionType> | undefined =
    errors?.actionData?.[idx];

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={2}>
        <Box sx={{ width: 400 }}>
          {currentAction.destUser && currentAction.destUser.length > 0 ? (
            <AvatarChip
              type="User"
              email={currentAction.destUser}
              onDelete={() => setValue(`actionData.${idx}.destUser`, '')}
              size="medium"
            />
          ) : (
            <Controller
              name={`actionData.${idx}.destUser`}
              control={control}
              render={() => (
                <UsersAutocomplete
                  newKey={`${autocompleteKey.current}`}
                  autocompleteValue={currentAction.destUser!}
                  setAutoCompleteValue={(newValue) =>
                    setValue(`actionData.${idx}.destUser`, newValue, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  errors={!!error?.destUser}
                  helperText={error?.destUser?.message || ' '}
                />
              )}
            />
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
